/**
 * Shared "Discover metrics" drawer + button used by anywhere the
 * operator types PromQL by hand (currently the SLO loop's
 * `kind=custom` rows in {@link SLOEditor}). Wraps the live
 * `/services/{id}/metric-catalog` scrape behind a button so we don't
 * fire it on every tab mount — the catalog is materialised only when
 * the operator clicks Discover, then cached per session by react-query
 * (see `useMetricCatalog`).
 *
 * Two pieces of output are intentionally separated:
 *
 *   - {@link MetricCatalogDrawer} renders the inspection drawer (table
 *     of name / type / help / labels with a text filter), plus a
 *     `<datalist>` whose id callers wire into their PromQL `<input>` /
 *     `<textarea>` via the `list` attribute for browser-native
 *     autocomplete.
 *   - {@link useMetricDiscovery} is the small state hook that drives
 *     both — it owns the "catalog enabled" gate (so opening the drawer
 *     enables the underlying query) and the drawer's open/close state,
 *     and returns the datalist id + opener so callers can wire a
 *     button anywhere convenient.
 *
 * Previously this UI lived inline in the now-removed
 * `ServiceCustomMetricsSection` and only autocompleted the standalone
 * Custom Metrics table — see the SLOEditor file header for why we
 * folded that section into the SLO loop instead of keeping it
 * separate.
 */
import { useMemo, useState } from "react";
import { RefreshCcw, Search } from "lucide-react";

import { useMetricCatalog } from "@/api/local/metric_catalog";
import type { MetricCatalogEntry } from "@/api/types";
import { Modal } from "@/components/Modal";
import { Pill } from "@/components/Pill";

interface DiscoveryHandle {
  /** id to thread into a PromQL `<input>` / `<textarea>` `list` attr. */
  datalistId: string;
  /** Metric names currently in the catalog (drives `<datalist>` options). */
  metricNames: string[];
  /** Renders the hidden `<datalist>` + the drawer. Mount once per scope. */
  catalogElements: React.ReactNode;
  /** Renders the "discover metrics" button. */
  discoverButton: React.ReactNode;
}

interface UseMetricDiscoveryOptions {
  serviceId: number;
  /**
   * Inert mode — render the wiring (datalist + drawer trigger) but
   * keep the button disabled. Used by the SLOEditor when the parent
   * scope is currently inheriting from its ancestor and the controls
   * are visually dimmed.
   */
  disabled?: boolean;
}

export function useMetricDiscovery(
  options: UseMetricDiscoveryOptions,
): DiscoveryHandle {
  const { serviceId, disabled } = options;
  const [enabled, setEnabled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const catalog = useMetricCatalog(serviceId, enabled);

  const datalistId = `metric-names-svc-${serviceId}`;
  const metricNames = useMemo(
    () => (catalog.data?.metrics ?? []).map((m) => m.name),
    [catalog.data],
  );

  const open = () => {
    setEnabled(true);
    setDrawerOpen(true);
  };

  const discoverButton = (
    <button
      type="button"
      className="btn btn--ghost btn--sm"
      onClick={open}
      disabled={disabled}
      title="Scrape one ready replica's /metrics and show what it exposes"
    >
      <Search />
      discover metrics
    </button>
  );

  const catalogElements = (
    <>
      <datalist id={datalistId}>
        {metricNames.map((n) => (
          <option key={n} value={n} />
        ))}
      </datalist>
      <Modal
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title="Discover metrics"
        subtitle={
          catalog.data ? (
            <>
              scraped from replica{" "}
              <code>#{catalog.data.replica_id}</code> at{" "}
              {new Date(catalog.data.scraped_at).toLocaleTimeString()}
            </>
          ) : (
            <span className="muted">
              live scrape of the workload's <code>/metrics</code> via
              Envoy
            </span>
          )
        }
        size="xl"
        headerExtras={
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => catalog.refetch()}
            disabled={catalog.isFetching}
            title="Re-scrape the replica's /metrics"
          >
            <RefreshCcw />
            refresh
          </button>
        }
      >
        <DiscoverMetricsBody
          loading={catalog.isFetching}
          error={catalog.error as Error | null}
          entries={catalog.data?.metrics ?? []}
        />
      </Modal>
    </>
  );

  return { datalistId, metricNames, catalogElements, discoverButton };
}

interface DiscoverMetricsBodyProps {
  loading: boolean;
  error: Error | null;
  entries: MetricCatalogEntry[];
}

function DiscoverMetricsBody({
  loading,
  error,
  entries,
}: DiscoverMetricsBodyProps) {
  const [filter, setFilter] = useState("");
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        e.help.toLowerCase().includes(q) ||
        e.labels.some((l) => l.toLowerCase().includes(q)),
    );
  }, [entries, filter]);

  if (loading && entries.length === 0) {
    return (
      <div className="muted" style={{ padding: 24, textAlign: "center" }}>
        scraping <code>/metrics</code> through Envoy…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flash">
        <strong>failed to discover metrics:</strong>{" "}
        <span className="mono">{error.message}</span>
        <p className="muted" style={{ fontSize: 11, marginTop: 6 }}>
          The controller needs at least one <strong>ready</strong>{" "}
          replica to scrape — start the service from the Replicas tab
          and refresh. Other failures usually mean Envoy can't reach
          the workload, or the workload doesn't expose{" "}
          <code>/metrics</code> on the configured port.
        </p>
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          marginBottom: 10,
        }}
      >
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="filter by name, help, or label"
          style={{ flex: 1 }}
        />
        <Pill tone="neutral">{filtered.length} / {entries.length}</Pill>
      </div>
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th style={{ minWidth: 220 }}>Name</th>
              <th style={{ width: 110 }}>Type</th>
              <th>Help</th>
              <th style={{ minWidth: 140 }}>Labels</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => (
              <tr key={e.name}>
                <td className="mono" style={{ fontSize: 12 }}>
                  {e.name}
                </td>
                <td>
                  <Pill tone="info">{e.type}</Pill>
                </td>
                <td style={{ fontSize: 12 }}>{e.help || "—"}</td>
                <td className="mono" style={{ fontSize: 11 }}>
                  {e.labels.length ? e.labels.join(", ") : "—"}
                </td>
              </tr>
            ))}
            {!filtered.length && (
              <tr>
                <td colSpan={4} className="empty">
                  no metrics match the filter
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
